import { Formik, Field } from "formik";
import MsgBoxModal from "../MsgBoxModal/msgbox-modal.component";
import Input from "../Input/input.component";
import {
	ADD_USER,
	GET_USERS,
	GET_DEPARTMENTS,
	GET_ROLES,
	ADD_AI_USER,
	EDIT_AI_EMAIL,
} from "./query";
import { useMutation, useQuery } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import { addAIUserValidationSchema, validateEmail } from "./validation-schema";
import MsgBoxSelect from "../MsgBoxSelect/msg-box-select.component";
import { useState, useEffect } from "react";
import UserInboxSelect from "../UserInboxSelect/user-inbox-select.component";
import { AIForm, AIFormColumn, Form, FormColumn } from "./styles";
import TextArea from "../TextArea/text-area.component";
import { borderStyle, borderWidth } from "polished";

const AddAIUser = ({ isOpen, close, rootStore }) => {
	const inboxStore = rootStore.getInboxStore();
	const [addAIUser, { loading, error }] = useMutation(ADD_AI_USER);
	const [editAIEmail, { loading: aiEditLoading, error: aiEditError }] =
		useMutation(EDIT_AI_EMAIL);
	const [dirty, setDirty] = useState(false);
	const [userRoleId, setUserRoleId] = useState(0);

	const {
		data: userRoles,
		error: roleError,
		loading: roleLoading,
	} = useQuery(GET_ROLES);

	useEffect(() => {
		if (userRoles && userRoles.msgbox_Role) {
			let userRole = userRoles.msgbox_Role.filter(
				(role) => role.Name == "User"
			);

			if (userRole && userRole[0]) {
				let userRoleId = userRole[0].RoleId;
				setUserRoleId(userRoleId);
			}
		}
	}, [userRoles]);

	const { addToast } = useToasts();

	const submit = ({ aiBotName, role, inboxes, aiPrompt }) => {
		addAIUser({
			variables: {
				aiBotName,
				role: { data: { RoleId: role.value } },
				inboxes: {
					data: inboxes.map((inbox) => ({ InboxId: inbox.value })),
				},

				aiPrompt,
				email: null,
			},
			onCompleted: (data) => {
				const uuidTemp =
					data?.insert_msgbox_User?.returning[0]?.UserUUID;
				let emailStr = `ai-${uuidTemp}@msgboxx.com`;
				editAIEmail({
					variables: {
						userId: data?.insert_msgbox_User?.returning[0]?.UserId,
						email: emailStr,
					},
				});
			},
			update: (cache, data) => {
				cache.modify({
					fields: {
						msgbox_User(existingResponses = []) {
							const newResponseRef = cache.writeQuery({
								data: data.data.insert_msgbox_User,
								query: GET_USERS,
							});
							return [...existingResponses, newResponseRef];
						},
					},
				});
				inboxStore.reloadPlans();
				addToast("Successfully added AI User", {
					appearance: "success",
					autoDismiss: true,
				});
				close();
			},
		});
	};

	const closeModal = () => {
		close();
		setDirty(false);
	};

	const onInputChange = (event, fn) => {
		setDirty(true);
		fn(event);
	};

	if (roleLoading) return null;

	if (roleError) {
		console.log("role err", roleError);
	}

	return (
		<MsgBoxModal
			aria={{
				labelledby: "heading",
				describedby: "full_description",
			}}
			isOpen={isOpen}
			cancel={closeModal}
			header="Add AI User"
			description="Use the form below to add an AI user"
			onRequestClose={closeModal}
			submitting={loading}
			submitButtonName={"Add AI User"}
			customModalOverlayStyles={{ overflow: "scroll" }}
			customModalContentStyles={{ overflowY: "auto", maxHeight: "80vh" }}
			submit={submit}
			submitId="add-user"
			submitButtonDisabled={dirty === false}
		>
			<div
				style={{
					overflowY: "auto",
					maxHeight: "60vh",
					paddingRight: "0.5rem",
					paddingLeft: "0.5rem",
				}}
			>
				<Formik
					initialValues={{
						aiBotName: "",
						role: { label: "User", value: userRoleId },
						inboxes: "",
						aiPrompt: "",
					}}
					validateOnBlur={false}
					validateOnChange={false}
					validationSchema={addAIUserValidationSchema}
					onSubmit={(values) => {
						submit(values);
					}}
				>
					{({
						handleSubmit,
						values,
						errors,
						handleChange,
						setFieldValue,
					}) => (
						<AIForm onSubmit={handleSubmit} id="add-user">
							<AIFormColumn>
								<Input
									placeholder="AI Name"
									required
									name="aiBotName"
									label="Name"
									onChange={(event) =>
										onInputChange(event, handleChange)
									}
									error={errors.aiBotName}
									value={values.aiBotName}
								/>

								<MsgBoxSelect
									key={3}
									placeholder="Select a role"
									required
									name="role"
									options={userRoles.msgbox_Role.map(
										(role) => ({
											label: role.Description,
											value: role.RoleId,
										})
									)}
									onChange={(options, action) => {
										setFieldValue("role", options);
										setDirty(true);
									}}
									label="Role"
									error={errors.role}
									value={values.role}
									hasTooltip
									toolTipText="Select this users role. An admin has the ability to change settings"
								/>
								<UserInboxSelect
									name="inboxes"
									placeholder="Select an inbox"
									required
									key={4}
									handleChange={(options, action) => {
										setFieldValue("inboxes", options);
										setDirty(true);
									}}
									label="Inboxes"
									hasTooltip
									toolTipText="Select the inboxes you would like this user to have access to"
									error={errors.inboxes}
									value={values.inboxes}
									rootStore={rootStore}
								/>
							</AIFormColumn>
							<AIFormColumn>
								<TextArea
									rows={16}
									style={{
										resize: "vertical",
										width: "100%",
									}}
									placeholder="Prompt"
									required
									name="aiPrompt"
									label="Prompt"
									onChange={(event) =>
										onInputChange(event, handleChange)
									}
									error={errors.aiPrompt}
									value={values.aiPrompt}
								/>
							</AIFormColumn>
						</AIForm>
					)}
				</Formik>
			</div>
		</MsgBoxModal>
	);
};

export default AddAIUser;
